import { Link } from "react-router-dom";
import { Calendar, ArrowRight, Tag } from "lucide-react";
import {
    NEPALI_KEYBOARD_BODY,
    SIXTY_WPM_BODY,
    ROMANIZED_VS_TRADITIONAL_BODY,
    WPM_VS_ACCURACY_BODY,
    WHY_TOUCH_TYPING_BODY,
    POSTURE_BODY,
} from "./blogBodies";

// Static blog posts — SEO-friendly content for AdSense readiness.
export const BLOG_POSTS = [
    {
        slug: "nepal-land-measurement-guide",
        category: "Utilities",
        title: "How to Calculate Land Area in Nepal: A Guide to Traditional vs. Modern Measurements",
        excerpt:
            "Understanding traditional land units like Ropani, Bigha, and Haath can be confusing. We unpack the math behind Nepalese property structures and show you how to convert them instantly...",
        image: "/images/blog/nepal-land-measurement.png",
        date: "2026-10-06",
        readingMinutes: 4,
        body: [
            { type: "h2", text: "Introduction" },
            {
                type: "p",
                text: "Understanding land measurement units in Nepal can be incredibly confusing for both locals and Nepalese living abroad. Depending on whether you are buying, selling, or inheriting property in the hilly regions or the flatlands of the Terai, the units of measurement change completely. Mastering these traditional systems is essential for accurate real estate valuation and documentation.",
            },
            { type: "h2", text: "The Two Measurement Systems of Nepal" },
            {
                type: "p",
                text: "Nepal primarily uses two distinct traditional systems alongside modern metric square formats:",
            },
            {
                type: "p",
                text: "1. The Ropani System (Hilly Regions & Kathmandu): In the hills and valleys, land is measured using Ropani, Aana, Paisa, and Daam. One Ropani is made up of 16 Aana, while a single Aana contains 4 Paisa.",
            },
            {
                type: "p",
                text: "2. The Bigha System (Terai Region): In the flat Terai plains, land is measured using Bigha, Kattha, and Dhur. One Bigha is equivalent to 20 Kattha, and one Kattha is divided into 20 Dhur.",
            },
            { type: "h2", text: "Why Accurate Conversion Matters" },
            {
                type: "p",
                text: "Most modern official blueprints, bank loan assessments, and architectural designs require measurements to be displayed in standard Square Feet or Square Meters. Manually calculating conversions between Ropani, Bigha, or traditional Haath (हात) into square footage often leads to expensive math errors.",
            },
            { type: "h2", text: "How to Convert Instantly" },
            {
                type: "p",
                text: "To avoid errors, convert each unit separately using fixed standard values and then add the results together. The table below lists the standard Ropani-system values in square feet, followed by a worked example.",
            },
            {
                type: "table",
                head: ["Unit", "Equivalent", "Square Feet"],
                rows: [
                    ["1 Ropani", "16 Aana", "5,476"],
                    ["1 Aana", "4 Paisa", "342.25"],
                    ["1 Paisa", "4 Daam", "85.56"],
                ],
            },
            { type: "example", title: "Worked example: 2 Ropani 8 Aana", text: "To calculate the total square footage of a land plot listed as 2 Ropani and 8 Aana, map the units out using standard conversion parameters. First, convert the Ropani component: 2 Ropani multiplied by 5,476 square feet equals 10,952 square feet. Next, convert the Aana component: 8 Aana multiplied by 342.25 square feet equals 2,738 square feet. Finally, combine both values: 10,952 plus 2,738 results in a total plot size of exactly 13,690 square feet." },
            {
                type: "p",
                text: "Always double-check the final figure against your official land ownership documents before using it for any sale, loan or legal purpose.",
            },
        ],
    },
    {
        slug: "why-touch-typing-still-matters-in-2026",
        category: "Fundamentals",
        title: "Why Touch Typing Still Matters in 2026",
        excerpt:
            "Voice input and AI writing tools are everywhere, yet accurate typing remains the fastest way to communicate on a keyboard. Here is why building the skill is worth every practice minute.",
        image:
            "https://images.unsplash.com/photo-1587829741301-dc798b83add3?auto=format&fit=crop&w=1200&q=60",
        date: "2026-01-14",
        readingMinutes: 6,
        body: WHY_TOUCH_TYPING_BODY,
    },
    {
        slug: "master-traditional-nepali-keyboard",
        category: "Nepali",
        title: "How to Master the Traditional Nepali (Devanagari) Keyboard",
        excerpt:
            "The Traditional Nepali layout maps Devanagari to your familiar QWERTY keys. Learn the finger-position philosophy and the small daily drills that build real muscle memory.",
        image:
            "https://images.unsplash.com/photo-1517430816045-df4b7de11d1d?auto=format&fit=crop&w=1200&q=60",
        date: "2026-01-22",
        readingMinutes: 8,
        body: NEPALI_KEYBOARD_BODY,
    },
    {
        slug: "wpm-vs-accuracy-what-to-optimize-first",
        category: "Practice",
        title: "WPM vs Accuracy: Which Should You Improve First?",
        excerpt:
            "Chasing raw speed leads to bad habits that plateau in weeks. Accuracy-first practice builds a durable ceiling that keeps climbing. We unpack the science behind smart drills.",
        image:
            "https://images.unsplash.com/photo-1526379095098-d400fd0bf935?auto=format&fit=crop&w=1200&q=60",
        date: "2026-01-30",
        readingMinutes: 5,
        body: WPM_VS_ACCURACY_BODY,
    },
    {
        slug: "posture-and-hand-position-for-long-typing-sessions",
        category: "Wellness",
        title: "Posture and Hand Position for Long Typing Sessions",
        excerpt:
            "Fifteen minutes of daily typing feels effortless. Ninety minutes exposes every ergonomic mistake. A clear guide to wrist, elbow, screen height, and micro-breaks.",
        image:
            "https://images.unsplash.com/photo-1585909695284-32d2985ac9c0?auto=format&fit=crop&w=1200&q=60",
        date: "2026-02-04",
        readingMinutes: 7,
        body: POSTURE_BODY,
    },
    {
        slug: "romanized-vs-traditional-nepali-typing",
        category: "Nepali",
        title: "Romanized vs Traditional Nepali Typing — What Is Right for You?",
        excerpt:
            "Both approaches produce beautiful Devanagari text, but they lead to very different long-term skills. A practical comparison for students, professionals, and content creators.",
        image:
            "https://images.unsplash.com/photo-1596526131083-e8c633c948d2?auto=format&fit=crop&w=1200&q=60",
        date: "2026-02-11",
        readingMinutes: 6,
        body: ROMANIZED_VS_TRADITIONAL_BODY,
    },
    {
        slug: "how-to-reach-60-wpm-in-30-days",
        category: "Practice",
        title: "How to Reach 60 WPM in Thirty Days — A Realistic Plan",
        excerpt:
            "A structured four-week plan that combines home-row drills, tempo tests, and weekly benchmark runs. No apps stacked on top of apps — just a clear ladder to a solid 60 WPM.",
        image:
            "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=1200&q=60",
        date: "2026-02-18",
        readingMinutes: 9,
        body: SIXTY_WPM_BODY,
    },
];

export default function Blog() {
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-10">
            <header>
                <p className="eyebrow">LipiTyping Blog</p>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-2 leading-tight">
                    Ideas, drills and stories for typists
                </h1>
                <p className="text-[var(--lt-muted)] mt-3 max-w-2xl text-sm sm:text-base">
                    Practical writing on typing craft — from Devanagari muscle memory to
                    accuracy-first practice loops and healthy habits at the keyboard.
                </p>
            </header>

            <section
                className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
                data-testid="blog-grid"
            >
                {BLOG_POSTS.map((p) => (
                    <article
                        key={p.slug}
                        className="glass rounded-2xl overflow-hidden group hover:border-[var(--lt-terracotta)] transition-colors"
                        data-testid={`blog-card-${p.slug}`}
                    >
                        <Link to={`/blog/${p.slug}`} className="block">
                            <div className="aspect-[16/10] w-full overflow-hidden bg-[var(--lt-bg-2)]">
                                <img
                                    src={p.image}
                                    alt={p.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                                />
                            </div>
                            <div className="p-5 sm:p-6">
                                <div className="flex items-center gap-3 text-xs">
                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[var(--lt-terracotta)]/15 text-[var(--lt-terracotta)] font-semibold">
                                        <Tag size={10} /> {p.category}
                                    </span>
                                    <span className="text-[var(--lt-muted)] inline-flex items-center gap-1">
                                        <Calendar size={10} />{" "}
                                        {new Date(p.date).toLocaleDateString("en-US", {
                                            month: "short",
                                            day: "numeric",
                                        })}
                                    </span>
                                    <span className="text-[var(--lt-muted)]">
                                        · {p.readingMinutes} min read
                                    </span>
                                </div>
                                <h2 className="text-lg sm:text-xl font-semibold mt-3 leading-snug">
                                    {p.title}
                                </h2>
                                <p className="text-sm text-[var(--lt-muted)] mt-2 leading-relaxed line-clamp-3">
                                    {p.excerpt}
                                </p>
                                <p className="mt-4 inline-flex items-center gap-1 text-sm text-[var(--lt-terracotta)] font-semibold">
                                    Read article{" "}
                                    <ArrowRight
                                        size={14}
                                        className="group-hover:translate-x-1 transition-transform"
                                    />
                                </p>
                            </div>
                        </Link>
                    </article>
                ))}
            </section>
        </div>
    );
}
